import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, ChevronDown, EyeOff, RefreshCw, Search } from 'lucide-react';
import {
  SAFE_DEFAULTS_MAX_COMPANIES,
  getDemoExposureFleet,
  type CompanyDemoExposure,
  type DemoExposureFleet,
  type DemoExposureSurface,
  type DemoSurfaceKind,
} from '../../../api/Navigation/moduleConfigAdmin';
import { navigationModules, pageTabGroups } from '../../../components/layout/navigationModules';
import { Checkbox } from '../../../components/ui/Checkbox';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import SafeDefaultsReview from './SafeDefaultsReview';
import { demoSurfaceId } from './useModuleConfigEditor';

/**
 * Fleet view of the Module Configuration page (CO-862): what the page shows while no tenant is open.
 *
 * It answers ONE question: which live tenants see which demo surfaces. One sentence states the answer,
 * the list below is sorted most exposed first, and everything else is a drill-down: a row opens to
 * name its surfaces, "By surface" turns the same answer round, and Open goes to the tenant, where the
 * grants are changed. Exposure follows the GRANT (CO-849), so nothing here counts a stored switch as
 * exposure; a stored ON row with no grant is named as such and shows nothing to anyone.
 *
 * The one bulk action is the stored-switch tidy-up (`SafeDefaultsReview`). It is reviewed inline, sets
 * switches OFF only and never touches a grant, so it cannot lower a tenant's visible count.
 *
 * Read once per visit and on Refresh. No polling: every SQL command on production is a paid row.
 */

/* ── Registry names of demo surfaces, shared with the review and the drift panel. ── */

export interface SurfaceName {
  label: string;
  /** The module that holds a page, the page that holds a tab. */
  owner?: string;
}
export type SurfaceNamer = (surface: { surfaceKind: DemoSurfaceKind; surfaceKey: string; moduleId?: string }) => SurfaceName;

/** Names a demo key from the navigation registry; a key the registry does not hold keeps its stored spelling. */
export function useSurfaceNamer(): SurfaceNamer {
  const { t } = useTranslation();
  return useMemo(() => {
    const modules = new Map<string, string>();
    const pages = new Map<string, SurfaceName>();
    const tabs = new Map<string, SurfaceName>();
    navigationModules.forEach(module => {
      const owner = t(module.title);
      modules.set(module.id.toLowerCase(), owner);
      (module.subModules ?? []).forEach(sub => modules.set(sub.id.toLowerCase(), t(sub.title)));
      [...module.pages, ...(module.subModules ?? []).flatMap(sub => sub.pages)].forEach(page => {
        const path = page.path.toLowerCase();
        if (!pages.has(path)) pages.set(path, { label: t(page.title), owner });
      });
    });
    pageTabGroups.forEach(group =>
      group.tabs.forEach(tab =>
        tabs.set(`${group.id}:${tab.id}`.toLowerCase(), { label: t(tab.title), owner: t(group.title) })
      )
    );
    return surface => {
      const key = surface.surfaceKey.toLowerCase();
      if (surface.surfaceKind === 'module') return { label: modules.get(key) ?? surface.surfaceKey };
      const found =
        surface.surfaceKind === 'tab' ? tabs.get(`${(surface.moduleId ?? '').toLowerCase()}:${key}`) : pages.get(key);
      return found ?? { label: surface.surfaceKey };
    };
  }, [t]);
}

/** The server sends UTC, sometimes without the zone marker. */
export const parseUtc = (value: string) => new Date(/([zZ]|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`);

/* ── The view ── */

export type FleetView = 'tenant' | 'surface';
/** Live: the owner's definition. Other: demo tenants and test companies. */
type Scope = 'live' | 'other' | 'all';
const SCOPES: Scope[] = ['live', 'other', 'all'];
const PAGE_SIZE = 50;

type FleetState =
  | { status: 'loading' }
  | { status: 'failed'; refused: boolean }
  /** `includeTest`: the read this answer belongs to. `refreshing`: a newer read of the same kind is in flight. */
  | { status: 'ready'; fleet: DemoExposureFleet; includeTest: boolean; refreshing: boolean };

interface FleetTableProps {
  /** `?view=surface` in the address. */
  view: FleetView;
  /** `?surface=<id>`: the surface opened in the By surface view. */
  surfaceId: string | null;
  onViewChange: (view: FleetView, surfaceId: string | null) => void;
  onOpenCompany: (companyId: number) => void;
}

const chipClass = (pressed: boolean) =>
  `inline-flex min-h-8 items-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 ${
    pressed
      ? 'border-primary bg-primary/10 text-primary'
      : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
  }`;
const buttonClass =
  'inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-card px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';
const primaryButtonClass =
  'inline-flex min-h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border border-primary bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';
/** One row: stacked and wrapping on a phone, seven aligned columns from `lg`. Never wider than the page. */
const rowGrid =
  'flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 lg:grid lg:grid-cols-[1.25rem_minmax(0,1fr)_4.5rem_6.5rem_13rem_6.5rem_4.5rem]';

const kindOf = (company: CompanyDemoExposure) => (company.isLive ? 'live' : company.isDemoTenant ? 'demo' : 'test');
const kindVariant = { live: 'info', demo: 'indigo', test: 'neutral' } as const;

/** What a company holds for one listed key, in the order that matters: seen, granted but hidden, only stored. */
function SurfaceState({ surface, namer }: { surface: DemoExposureSurface; namer: SurfaceNamer }) {
  const { t } = useTranslation();
  if (surface.isVisible) {
    return (
      <StatusBadge variant="warning" size="sm" withDot className="shrink-0">
        {t('moduleConfig.fleet.state.visible')}
      </StatusBadge>
    );
  }
  if (surface.isGranted) {
    const module = surface.hiddenByModuleId
      ? namer({ surfaceKind: 'module', surfaceKey: surface.hiddenByModuleId }).label
      : null;
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
        <EyeOff aria-hidden="true" className="h-3 w-3" />
        {module ? t('moduleConfig.fleet.state.grantedHiddenBy', { module }) : t('moduleConfig.fleet.state.grantedHidden')}
      </span>
    );
  }
  return <span className="shrink-0 text-xs text-muted-foreground">{t('moduleConfig.fleet.state.storedOnly')}</span>;
}

interface TenantRowProps {
  company: CompanyDemoExposure;
  /** Number of keys on the demo list. */
  total: number;
  /** Already formatted for the reader, or the word for "never". */
  lastChanged: string;
  open: boolean;
  selected: boolean;
  /** True while a review is open: the selection it was built from must not move. */
  locked: boolean;
  namer: SurfaceNamer;
  onToggleOpen: () => void;
  onToggleSelect: () => void;
  onOpen: () => void;
}

/** One tenant: the answer on the line, the surfaces behind it one press away. */
function TenantRow({ company, total, lastChanged, open, selected, locked, namer, onToggleOpen, onToggleSelect, onOpen }: TenantRowProps) {
  const { t } = useTranslation();
  const kind = kindOf(company);
  const alarming = company.isLive && company.visibleCount > 0;
  const panelId = `module-config-fleet-surfaces-${company.companyId}`;
  return (
    <li
      data-testid={`module-config-fleet-row-${company.companyId}`}
      data-visible-count={company.visibleCount}
      data-kind={kind}
    >
      <div className={rowGrid}>
        <Checkbox
          checked={selected}
          disabled={locked}
          aria-label={t('moduleConfig.fleet.selectTenant', { name: company.companyName })}
          data-testid={`module-config-fleet-select-${company.companyId}`}
          onChange={onToggleSelect}
        />
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={`module-config-fleet-expand-${company.companyId}`}
          onClick={onToggleOpen}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-start text-sm text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none ${
              open ? '' : '-rotate-90 rtl:rotate-90'
            }`}
          />
          <span className="min-w-0 truncate font-medium">{company.companyName}</span>
          <span dir="ltr" className="shrink-0 text-xs tabular-nums text-muted-foreground">
            #{company.companyId}
          </span>
        </button>
        <span className="text-xs text-muted-foreground">{t(`moduleConfig.fleet.tier.${company.tier}`)}</span>
        <span>
          <StatusBadge variant={kindVariant[kind]} size="sm">
            {t(`moduleConfig.fleet.kind.${kind}`)}
          </StatusBadge>
        </span>
        <span className="flex min-w-0 flex-col text-xs">
          <span
            className={`inline-flex items-center gap-1 ${
              alarming ? 'font-medium text-warning-strong' : 'text-foreground'
            }`}
          >
            {alarming && <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />}
            {t('moduleConfig.fleet.demoOn', { on: company.visibleCount, total })}
          </span>
          {company.staleOnCount > 0 && (
            <span className="text-muted-foreground">
              {t('moduleConfig.fleet.staleOn', { total: company.staleOnCount })}
            </span>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          <span className="lg:hidden">{t('moduleConfig.fleet.col.lastChanged')} </span>
          {lastChanged}
        </span>
        <button
          type="button"
          className={`${buttonClass} ms-auto lg:ms-0`}
          disabled={locked}
          aria-label={t('moduleConfig.fleet.openLabel', { name: company.companyName })}
          data-testid={`module-config-fleet-open-${company.companyId}`}
          onClick={onOpen}
        >
          {t('moduleConfig.fleet.open')}
        </button>
      </div>
      <div id={panelId} hidden={!open} className="border-t border-border bg-muted px-4 py-2">
        {open &&
          (company.surfaces.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('moduleConfig.fleet.noSurfaces')}</p>
          ) : (
            <ul className="space-y-1.5">
              {company.surfaces.map(surface => {
                const name = namer(surface);
                return (
                  <li
                    key={demoSurfaceId(surface)}
                    data-testid={`module-config-fleet-surface-${company.companyId}-${demoSurfaceId(surface)}`}
                    data-visible={surface.isVisible ? 'true' : 'false'}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                  >
                    <span className="min-w-0 truncate text-sm text-foreground">{name.label}</span>
                    {name.label !== surface.surfaceKey && (
                      // A route or a storage key, so it reads left to right in every language.
                      <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                        {surface.surfaceKey}
                      </span>
                    )}
                    <SurfaceState surface={surface} namer={namer} />
                  </li>
                );
              })}
            </ul>
          ))}
      </div>
    </li>
  );
}

interface PivotGroup {
  id: string;
  surface: DemoExposureSurface;
  companies: CompanyDemoExposure[];
  name: SurfaceName;
}
interface SurfacePivotProps {
  /** Only the surfaces somebody in scope can see, most seen first. */
  groups: PivotGroup[];
  total: number;
  surfaceId: string | null;
  onViewChange: (view: FleetView, surfaceId: string | null) => void;
  onOpenCompany: (companyId: number) => void;
}

/** The same answer turned round: per surface, the tenants whose users see it. */
function SurfacePivot({ groups, total, surfaceId, onViewChange, onOpenCompany }: SurfacePivotProps) {
  const { t } = useTranslation();
  return (
    <>
      {groups.length === 0 ? (
        <p data-testid="module-config-fleet-empty" className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t('moduleConfig.fleet.noSurfaceVisible')}
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {groups.map(group => {
            const open = surfaceId === group.id;
            const panelId = `module-config-fleet-tenants-${group.id}`;
            return (
              <li key={group.id} data-testid={`module-config-fleet-pivot-${group.id}`} data-company-count={group.companies.length}>
                <button
                  type="button"
                  data-testid={`module-config-fleet-pivot-toggle-${group.id}`}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => onViewChange('surface', open ? null : group.id)}
                  className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none ${
                      open ? '' : '-rotate-90 rtl:rotate-90'
                    }`}
                  />
                  <span className="min-w-0 truncate text-sm font-medium text-foreground">{group.name.label}</span>
                  {group.name.label !== group.surface.surfaceKey && (
                    <span dir="ltr" className="min-w-0 truncate font-mono text-xs text-muted-foreground">
                      {group.surface.surfaceKey}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground sm:ms-auto">
                    {t('moduleConfig.surface.visibleTo', { count: group.companies.length })}
                  </span>
                </button>
                <ul id={panelId} hidden={!open} className="space-y-1.5 border-t border-border bg-muted px-4 py-2">
                  {open &&
                    group.companies.map(company => (
                      <li key={company.companyId} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="min-w-0 truncate text-sm text-foreground">{company.companyName}</span>
                        <span dir="ltr" className="text-xs tabular-nums text-muted-foreground">#{company.companyId}</span>
                        <StatusBadge variant={kindVariant[kindOf(company)]} size="sm">
                          {t(`moduleConfig.fleet.kind.${kindOf(company)}`)}
                        </StatusBadge>
                        <button
                          type="button"
                          className={`${buttonClass} ms-auto`}
                          aria-label={t('moduleConfig.fleet.openLabel', { name: company.companyName })}
                          onClick={() => onOpenCompany(company.companyId)}
                        >
                          {t('moduleConfig.fleet.open')}
                        </button>
                      </li>
                    ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
      {total > groups.length && (
        <p className="mt-3 text-xs text-muted-foreground">
          {t('moduleConfig.surface.unseen', { count: total - groups.length })}
        </p>
      )}
    </>
  );
}

export default function FleetTable({ view, surfaceId, onViewChange, onOpenCompany }: FleetTableProps) {
  const { t, i18n } = useTranslation();
  const namer = useSurfaceNamer();

  const [scope, setScope] = useState<Scope>('live');
  /** Test companies are asked for only when a chip needs them: dev holds over a thousand. */
  const includeTest = scope !== 'live';
  const [state, setState] = useState<FleetState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [query, setQuery] = useState('');
  const [exposedOnly, setExposedOnly] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // A refresh keeps the list on screen: the review panel sits in it and must survive its own reload.
    setState(previous =>
      previous.status === 'ready' && previous.includeTest === includeTest
        ? { ...previous, refreshing: true }
        : { status: 'loading' }
    );
    (async () => {
      const result = await getDemoExposureFleet(includeTest);
      if (cancelled) return;
      setState(
        result.ok
          ? { status: 'ready', fleet: result.value, includeTest, refreshing: false }
          : { status: 'failed', refused: result.status === 401 || result.status === 403 }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [includeTest, reloadToken]);

  // A narrower list starts from its first page again.
  useEffect(() => setLimit(PAGE_SIZE), [scope, query, exposedOnly]);

  // An answer read for the other chip is not shown: for one render it would list the wrong group.
  const fleet = state.status === 'ready' && state.includeTest === includeTest ? state.fleet : null;
  const refreshing = state.status === 'ready' && state.refreshing;
  const inScope = useMemo(
    () => (fleet?.companies ?? []).filter(c => (scope === 'live' ? c.isLive : scope === 'other' ? !c.isLive : true)),
    [fleet, scope]
  );
  const needle = query.trim().toLowerCase();
  const matching = inScope.filter(
    c =>
      (!exposedOnly || c.visibleCount > 0) &&
      (needle === '' || c.companyName.toLowerCase().includes(needle) || `#${c.companyId}`.includes(needle))
  );
  const shown = matching.slice(0, limit);

  /** The same answer turned round: each surface somebody in scope can see, and who. */
  const bySurface = useMemo(() => {
    type Group = { surface: DemoExposureSurface; companies: CompanyDemoExposure[] };
    const groups = new Map<string, Group>();
    inScope.forEach(company =>
      company.surfaces.forEach(surface => {
        if (!surface.isVisible) return;
        const id = demoSurfaceId(surface);
        const group: Group = groups.get(id) ?? { surface, companies: [] };
        group.companies.push(company);
        groups.set(id, group);
      })
    );
    return [...groups.entries()]
      .map(([id, group]): PivotGroup => ({ id, ...group, name: namer(group.surface) }))
      .sort((a, b) => b.companies.length - a.companies.length || a.name.label.localeCompare(b.name.label));
  }, [inScope, namer]);

  const toggleIn = (set: ReadonlySet<number>, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const allShownSelected = shown.length > 0 && shown.every(c => selected.has(c.companyId));
  const formatDate = (value?: string) =>
    value
      ? new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' }).format(parseUtc(value))
      : t('moduleConfig.fleet.never');

  if (state.status === 'failed') {
    return (
      <div
        role="alert"
        data-testid="module-config-fleet-load-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1">
          {state.refused ? t("You don't have permission to perform this action.") : t('moduleConfig.fleet.loadFailed')}
        </span>
        {!state.refused && (
          <button type="button" className={buttonClass} onClick={() => setReloadToken(n => n + 1)}>
            {t('Retry')}
          </button>
        )}
      </div>
    );
  }

  const exposed = fleet?.exposedLiveCompanyCount ?? 0;
  const total = fleet?.surfaceCount ?? 0;

  return (
    <section data-testid="module-config-fleet" aria-labelledby="module-config-fleet-summary" aria-busy={fleet === null || refreshing}>
      <div className="mb-3 flex flex-wrap items-start gap-x-3 gap-y-2">
        {/* The answer, in one sentence. Words and an icon, never colour alone. */}
        <h2
          id="module-config-fleet-summary"
          data-testid="module-config-fleet-summary"
          data-exposed-live={fleet ? exposed : undefined}
          className={`flex min-w-0 flex-1 items-start gap-2 text-base font-medium ${
            exposed > 0 ? 'text-warning-strong' : 'text-foreground'
          }`}
        >
          {exposed > 0 && <AlertTriangle aria-hidden="true" className="mt-1 h-4 w-4 shrink-0" />}
          <span>
            {fleet === null && t('moduleConfig.fleet.loading')}
            {fleet !== null && exposed === 0 && t('moduleConfig.fleet.summaryNone', { total: fleet.liveCompanyCount })}
            {fleet !== null && exposed > 0 && t('moduleConfig.fleet.summary', { count: exposed, total: fleet.liveCompanyCount })}
          </span>
        </h2>
        <button
          type="button"
          className={buttonClass}
          data-testid="module-config-fleet-refresh"
          disabled={fleet === null || refreshing || reviewing}
          onClick={() => setReloadToken(n => n + 1)}
        >
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5 text-muted-foreground" />
          {t('moduleConfig.fleet.refresh')}
        </button>
      </div>

      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative min-w-0 lg:w-64">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground ltr:left-3 rtl:right-3"
          />
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('moduleConfig.picker.searchPlaceholder')}
            aria-label={t('moduleConfig.fleet.search')}
            data-testid="module-config-fleet-search"
            disabled={view === 'surface'}
            className="h-9 w-full rounded-lg border border-input bg-card pe-3 ps-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <div role="group" aria-label={t('moduleConfig.fleet.scopeLabel')} className="flex flex-wrap items-center gap-1.5">
          {SCOPES.map(option => (
            <button
              key={option}
              type="button"
              aria-pressed={scope === option}
              disabled={reviewing}
              data-testid={`module-config-fleet-scope-${option}`}
              className={chipClass(scope === option)}
              onClick={() => setScope(option)}
            >
              {t(`moduleConfig.fleet.chip.${option}`)}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={exposedOnly}
            disabled={view === 'surface'}
            data-testid="module-config-fleet-exposed-only"
            className={chipClass(exposedOnly)}
            onClick={() => setExposedOnly(previous => !previous)}
          >
            {t('moduleConfig.fleet.exposedOnly')}
          </button>
        </div>
        <div role="group" aria-label={t('moduleConfig.fleet.viewLabel')} className="flex items-center gap-1.5 lg:ms-auto">
          {(['tenant', 'surface'] as const).map(option => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              disabled={reviewing}
              data-testid={`module-config-fleet-view-${option}`}
              className={chipClass(view === option)}
              onClick={() => onViewChange(option, null)}
            >
              {t(option === 'tenant' ? 'moduleConfig.fleet.byTenant' : 'moduleConfig.fleet.bySurface')}
            </button>
          ))}
        </div>
      </div>

      {fleet === null && (
        <div role="status" aria-label={t('moduleConfig.fleet.loading')} className="animate-pulse space-y-2 motion-reduce:animate-none">
          {[0, 1, 2, 3, 4].map(n => (
            <div key={n} className="h-10 rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {fleet !== null && view === 'tenant' && (
        <>
          {selected.size > 0 && !reviewing && (
            <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-muted px-4 py-2">
              <span data-testid="module-config-fleet-selected" className="text-sm font-medium text-foreground">
                {t('moduleConfig.fleet.selected', { count: selected.size })}
              </span>
              {selected.size > SAFE_DEFAULTS_MAX_COMPANIES && (
                <span className="text-xs text-muted-foreground">
                  {t('moduleConfig.fleet.bulk.tooMany', { max: SAFE_DEFAULTS_MAX_COMPANIES })}
                </span>
              )}
              <div className="flex flex-wrap items-center gap-2 sm:ms-auto">
                <button type="button" className={buttonClass} onClick={() => setSelected(new Set())}>
                  {t('moduleConfig.fleet.clearSelection')}
                </button>
                <button
                  type="button"
                  className={primaryButtonClass}
                  data-testid="module-config-fleet-review"
                  disabled={selected.size > SAFE_DEFAULTS_MAX_COMPANIES}
                  onClick={() => setReviewing(true)}
                >
                  {t('moduleConfig.fleet.bulk.review', { count: selected.size })}
                </button>
              </div>
            </div>
          )}
          {reviewing && (
            <SafeDefaultsReview
              companyIds={[...selected]}
              namer={namer}
              onApplied={() => setReloadToken(n => n + 1)}
              onClose={() => {
                setReviewing(false);
                setSelected(new Set());
              }}
            />
          )}

          {matching.length === 0 ? (
            <p data-testid="module-config-fleet-empty" className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
              {inScope.length === 0 ? t('moduleConfig.fleet.emptyScope') : t('moduleConfig.fleet.noMatch')}
            </p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border bg-card">
              <div className={`${rowGrid} border-b border-border bg-muted text-xs font-medium text-muted-foreground`}>
                <Checkbox
                  checked={allShownSelected}
                  disabled={reviewing}
                  aria-label={t('moduleConfig.fleet.selectShown')}
                  data-testid="module-config-fleet-select-shown"
                  onChange={() =>
                    setSelected(allShownSelected ? new Set() : new Set(shown.map(company => company.companyId)))
                  }
                />
                {/* Column words for the aligned layout; on a phone each cell reads by itself. */}
                <span aria-hidden="true" className="hidden lg:block">{t('moduleConfig.fleet.col.tenant')}</span>
                <span aria-hidden="true" className="hidden lg:block">{t('moduleConfig.fleet.col.tier')}</span>
                <span aria-hidden="true" className="hidden lg:block">{t('moduleConfig.fleet.col.kind')}</span>
                <span aria-hidden="true" className="hidden lg:block">{t('moduleConfig.fleet.col.demoOn')}</span>
                <span aria-hidden="true" className="hidden lg:block">{t('moduleConfig.fleet.col.lastChanged')}</span>
                <span className="text-xs lg:hidden">{t('moduleConfig.fleet.selectShown')}</span>
              </div>
              <ul className="divide-y divide-border">
                {shown.map(company => (
                  <TenantRow
                    key={company.companyId}
                    company={company}
                    total={total}
                    lastChanged={formatDate(company.lastChangedUtc)}
                    open={expanded.has(company.companyId)}
                    selected={selected.has(company.companyId)}
                    locked={reviewing}
                    namer={namer}
                    onToggleOpen={() => setExpanded(previous => toggleIn(previous, company.companyId))}
                    onToggleSelect={() => setSelected(previous => toggleIn(previous, company.companyId))}
                    onOpen={() => onOpenCompany(company.companyId)}
                  />
                ))}
              </ul>
            </div>
          )}
          {matching.length > shown.length && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>{t('moduleConfig.fleet.showing', { shown: shown.length, total: matching.length })}</span>
              <button
                type="button"
                className={buttonClass}
                data-testid="module-config-fleet-more"
                onClick={() => setLimit(previous => previous + PAGE_SIZE)}
              >
                {t('moduleConfig.fleet.showMore')}
              </button>
            </div>
          )}
        </>
      )}

      {fleet !== null && view === 'surface' && (
        <SurfacePivot groups={bySurface} total={total} surfaceId={surfaceId} onViewChange={onViewChange} onOpenCompany={onOpenCompany} />
      )}
    </section>
  );
}
